import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import { PhoneNumberValidatorEnum } from "@hopper-b2b/types";
import {
  ActionLinks,
  B2BTextField,
  type IActionLink,
  PhoneInputField,
  Slot,
} from "@hopper-b2b/ui";
import {
  emailRegex as defaultEmailRegex,
  phoneRegex as defaultPhoneRegex,
  validateEmail,
} from "@hopper-b2b/utilities";
import { Box, Button, Grid, Typography } from "@material-ui/core";
import type { Variant } from "@material-ui/core/styles/createTypography";
import clsx from "clsx";
import type { IContactInfo } from "../../../../types/common";
import "./styles.scss";
import { updateContactInfo } from "./updateUtils";
import { validatePhone } from "./validateUtils";

const CONTACT_INFO_UPDATE_DELAY = 150;

export interface IContactInfoFormProps {
  className?: string;
  isMobile?: boolean;
  title?: string;
  subtitle?: string;
  contactInfo: IContactInfo | null | undefined;
  onContactInfoChange?: (contactInfo: IContactInfo) => void;
  disabled?: boolean;
  hideEmailAddress?: boolean;
  hidePhoneNumber?: boolean;
  actions?: IActionLink[];
  headerVariant?: Variant;
  header?: boolean;
  showEditAction?: boolean;
  onEdit?: () => void;
  inHotel?: boolean;
  disableRipple?: boolean;
  validate?: boolean;
  emailRegex?: RegExp;
  phoneRegex?: RegExp;
  phoneInputLabel?: string | ReactNode;
  checkPhoneMinLength?: boolean;
  minPhoneLength?: number;
  requireCountryCode?: boolean;
  onFocusEmail?: (
    value: string,
    event?: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onBlurEmail?: (value: string) => void;
}

const getInitialPhoneNumberError = (
  contactInfo: IContactInfo | null | undefined,
  requireCountryCode: boolean | undefined,
  phoneRegex: RegExp,
  t: (key: string) => string
): string => {
  if (!contactInfo || !contactInfo.phoneNumber) return "";

  if (requireCountryCode && !contactInfo.countryCode) {
    return t("contactInfoCountryCodeError");
  }

  return phoneRegex.test(contactInfo.phoneNumber)
    ? ""
    : t("contactInfoPhoneError");
};

export const ContactInfoForm = ({
  contactInfo,
  onContactInfoChange,
  validate = true,
  emailRegex,
  inHotel,
  phoneRegex = defaultPhoneRegex,
  phoneInputLabel,
  checkPhoneMinLength,
  minPhoneLength,
  requireCountryCode,
  ...rest
}: IContactInfoFormProps) => {
  const { t, brand } = useI18nContext();

  const [countryCode, setCountryCode] = useState(
    contactInfo?.countryCode || brand.preferredAreaCode || ""
  );
  const [phoneNumber, setPhoneNumber] = useState(
    contactInfo?.phoneNumber || ""
  );
  const [email, setEmail] = useState(contactInfo?.email || "");
  const contactInfoRef = useRef<IContactInfo>({
    countryCode: contactInfo?.countryCode || brand.preferredAreaCode || "",
    phoneNumber: contactInfo?.phoneNumber || "",
    email: contactInfo?.email || "",
  });
  const onContactInfoChangeRef = useRef(onContactInfoChange);
  const contactInfoUpdateTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const scheduleContactInfoChange = useCallback(
    (nextContactInfo: IContactInfo) => {
      clearTimeout(contactInfoUpdateTimeoutRef.current);
      contactInfoUpdateTimeoutRef.current = setTimeout(() => {
        contactInfoUpdateTimeoutRef.current = undefined;
        onContactInfoChangeRef.current?.(nextContactInfo);
      }, CONTACT_INFO_UPDATE_DELAY);
    },
    []
  );

  useEffect(() => {
    onContactInfoChangeRef.current = onContactInfoChange;
  }, [onContactInfoChange]);

  useEffect(
    () => () => {
      if (contactInfoUpdateTimeoutRef.current === undefined) return;

      clearTimeout(contactInfoUpdateTimeoutRef.current);
      contactInfoUpdateTimeoutRef.current = undefined;
      onContactInfoChangeRef.current?.(contactInfoRef.current);
    },
    []
  );

  const [emailError, setEmailError] = useState(
    contactInfo && contactInfo.email
      ? (emailRegex ?? defaultEmailRegex).test(contactInfo.email)
        ? ""
        : t("contactInfoEmailError")
      : ""
  );
  const [phoneNumberError, setPhoneNumberError] = useState(
    getInitialPhoneNumberError(contactInfo, requireCountryCode, phoneRegex, t)
  );

  useEffect(() => {
    if (requireCountryCode && !countryCode) {
      setPhoneNumberError(t("contactInfoCountryCodeError"));
    }
  }, [countryCode, requireCountryCode, t]);

  const updateContactInfoCallback = useCallback(
    (change: Partial<IContactInfo>) => {
      updateContactInfo({
        callback: scheduleContactInfoChange,
        contactInfoRef,
        change,
      });
    },
    [scheduleContactInfoChange]
  );

  const onCountryCodeChanged = useCallback(
    (_countryCode: string) => {
      setCountryCode(_countryCode);
      updateContactInfoCallback({ countryCode: _countryCode });
    },
    [updateContactInfoCallback]
  );

  const validateEmailCallback = useCallback(
    (_email: string) => {
      validateEmail({
        validate,
        emailRegex,
        email: _email,
        onInvalid: (key: string) => key && setEmailError(t(key)),
        onValid: () => setEmailError(""),
      });
    },
    [validate, emailRegex, t]
  );

  const onEmailChanged = useCallback(
    (_email: string) => {
      setEmail(_email);
      validateEmailCallback(_email);

      updateContactInfoCallback({ email: _email });
    },
    [updateContactInfoCallback, validateEmailCallback]
  );

  const validatePhoneNumberCallback = useCallback(
    (_phoneNumber: string) => {
      validatePhone({
        validate,
        phoneRegex,
        minPhoneLength,
        checkPhoneMinLength,
        phoneNumber: _phoneNumber,
        onInvalid: (key: string) => {
          key && setPhoneNumberError(t(key));
        },
        onValid: () => setPhoneNumberError(""),
      });
    },
    [validate, phoneRegex, minPhoneLength, checkPhoneMinLength, t]
  );

  const onPhoneNumberChanged = useCallback(
    (
      _phoneNumber: string,
      _countryCode: string,
      customIsValid?: (number: string) => PhoneNumberValidatorEnum
    ) => {
      if (requireCountryCode && !_countryCode) {
        setPhoneNumberError(t("contactInfoCountryCodeError"));
      } else if (customIsValid) {
        switch (customIsValid(_phoneNumber)) {
          case PhoneNumberValidatorEnum.CUSTOM_ERROR:
            setPhoneNumberError(t("invalidPhoneNumberFormat"));
            break;
          case PhoneNumberValidatorEnum.VALID:
            setPhoneNumberError("");
            break;
          case PhoneNumberValidatorEnum.DEFAULT_ERROR:
            setPhoneNumberError(t("contactInfoPhoneError"));
            break;
          case PhoneNumberValidatorEnum.NOT_VALIDATED:
            validatePhoneNumberCallback(_phoneNumber);
            break;
        }
      } else {
        validatePhoneNumberCallback(_phoneNumber);
      }

      setPhoneNumber(_phoneNumber);
      setCountryCode(_countryCode);

      updateContactInfoCallback({
        countryCode: _countryCode,
        phoneNumber: _phoneNumber,
      });
    },
    [
      t,
      updateContactInfoCallback,
      validatePhoneNumberCallback,
      requireCountryCode,
    ]
  );

  return (
    <Box
      className={clsx("contact-info-form-container", rest.className, {
        mobile: rest.isMobile,
        disabled: rest.disabled,
        "has-error": !!emailError || !!phoneNumberError,
        "has-actions": !!rest.actions,
        "display-only": rest.disabled,
        hotel: inHotel,
      })}
    >
      <Slot
        id="checkout-contact-info-form"
        countryCode={countryCode}
        setCountryCode={onCountryCodeChanged}
        phone={phoneNumber}
        phoneError={phoneNumberError}
        onPhoneNumberChanged={onPhoneNumberChanged}
        email={email}
        emailError={emailError}
        onEmailChanged={onEmailChanged}
        {...rest}
        component={
          <ContactInfoFormInternal
            countryCode={countryCode}
            phone={phoneNumber}
            phoneInputLabel={phoneInputLabel}
            phoneError={phoneNumberError}
            onPhoneNumberChanged={onPhoneNumberChanged}
            email={email}
            emailError={emailError}
            onEmailChanged={onEmailChanged}
            {...rest}
          />
        }
      />
    </Box>
  );
};

export type ContactInfoFormInternalProps = Omit<
  IContactInfoFormProps,
  | "contactInfo"
  | "onContactInfoChange"
  | "validate"
  | "emailRegex"
  | "inHotel"
  | "phoneRegex"
  | "checkPhoneMinLength"
  | "minPhoneLength"
> & {
  countryCode: string;
  phone: string;
  phoneError: string;
  phoneInputLabel?: string | ReactNode;
  onPhoneNumberChanged: (
    _phoneNumber: string,
    _countryCode: string,
    customIsValid?: (number: string) => PhoneNumberValidatorEnum
  ) => void;
  email: string;
  emailError: string;
  onEmailChanged: (_email: string) => void;
  onFocusEmail?: (
    value: string,
    event?: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onBlurEmail?: (value: string) => void;
};

const ContactInfoFormInternal = ({
  // From IContactInfoFormProps
  title,
  subtitle,
  className,
  isMobile,
  disabled,
  hideEmailAddress = false,
  hidePhoneNumber = false,
  actions,
  headerVariant = "h3",
  header = true,
  showEditAction = false,
  onEdit,
  disableRipple,
  // From ContactInfoFormInternalProps
  countryCode,
  phone,
  phoneError,
  phoneInputLabel,
  onPhoneNumberChanged,
  email,
  emailError,
  onEmailChanged,
  onFocusEmail,
  onBlurEmail,
}: ContactInfoFormInternalProps) => {
  const { t } = useI18nContext();
  return (
    <>
      {header ? (
        <Grid
          container
          spacing={2}
          alignItems="flex-start"
          className="header-contact-info"
        >
          <Grid item className="contact-info-form-description">
            {title ? (
              <Typography
                className="title"
                variant={headerVariant ?? (isMobile ? "caption" : "h3")}
              >
                {title}
              </Typography>
            ) : null}
            {subtitle ? (
              <Typography variant="body2">{subtitle}</Typography>
            ) : null}
          </Grid>
          <Grid item xs />
          {showEditAction && (
            <Grid item>
              <Button
                className="edit-action"
                disableRipple={disableRipple}
                variant="text"
                color="primary"
                onClick={onEdit}
              >
                {t("edit")}
              </Button>
            </Grid>
          )}
        </Grid>
      ) : null}
      <Typography
        className={clsx("contact-info-form-required")}
        variant="body2"
      >
        {`${t("requiredField")}*`}
      </Typography>
      <Box className={clsx("contact-info-form", className)}>
        {!hidePhoneNumber && (
          <Grid item>
            <PhoneInputField
              label={phoneInputLabel ?? `${t("phoneNumber")} *`}
              defaultValue={phone}
              countryCode={countryCode}
              errorHelper={phoneError}
              onChange={onPhoneNumberChanged}
              disabled={disabled}
            />
          </Grid>
        )}
        {!hideEmailAddress && (
          <Grid item>
            <B2BTextField
              id="contact-email-input-field"
              className="email-input-field"
              label={t("email")}
              required
              value={email}
              errorHelper={emailError}
              onChange={onEmailChanged}
              onFocus={onFocusEmail}
              onBlur={onBlurEmail}
              disabled={disabled}
              type="email"
            />
          </Grid>
        )}
        {actions && (
          <Grid item>
            <ActionLinks
              className="action-links-contact-info"
              actions={actions}
            />
          </Grid>
        )}
      </Box>
    </>
  );
};
